import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useWorkspaceTabNavigation } from '../context/WorkspaceTabsContext';
import ticketService from '../services/ticketService';
import { notifyMerchantUpdated } from '../utils/merchantEvents';
import merchantService from '../services/merchantService';
import { ArrowLeftIcon } from '../components/Icons';
import WorkflowActionPanel from '../components/workflow/WorkflowActionPanel';
import RecordUpdatePanel from '../components/tickets/RecordUpdatePanel';
import RecordCreationPanel from '../components/tickets/RecordCreationPanel';
import TicketRequestEditor from '../components/tickets/TicketRequestEditor';
import TicketConversation from '../components/tickets/TicketConversation';
import TicketCommentComposer from '../components/tickets/TicketCommentComposer';
import TicketActionDialog from '../components/tickets/TicketActionDialog';
import TicketSummary from '../components/tickets/TicketSummary';
import TicketFieldList from '../components/tickets/TicketFieldList';
import ProviderConfigSummary from '../components/tickets/ProviderConfigSummary';
import './TicketDetail.css';
import { buildTicketActionPayload, isCollaborativeProviderRequest, isCollaborativeTicket, getTicketProviderDefinition, isPaymentGatewayTicket } from '../utils/ticketRequests';
import { isRecordCreationTicketType } from '../utils/recordCreationTickets';
import { getProviderCategoryContext, getProviderReferenceLabel } from '../utils/providerCategory';
import { getTicketCreatorName } from '../utils/ticketQueue';

const ACTION_LABELS = {
  assign: 'Assign / hand off',
  start_work: 'Start Work',
  developer_done: 'IT Setup Done',
  credentials_ready: 'Credentials Validated',
  send_to_qa: 'Send To QA',
  report_issue: 'Send To IT',
  it_fixed: 'IT Fixed',
  qa_pass: 'QA Passed',
  qa_fail: 'QA Failed',
  reopen: 'Reopen Ticket',
  cancel: 'Cancel Ticket',
  updated: 'Save Assignment',
  created: 'Ticket Created',
  cancelled: 'Ticket Cancelled',
  costing_rate_approved: 'Costing Rate Approved',
  costing_rate_rejected: 'Costing Rate Rejected',
  provider_change_approved: 'Provider Change Approved',
  provider_change_rejected: 'Provider Change Rejected',
  provider_change_submitted: 'Provider Change Applied'
};

const LABEL_OVERRIDES = {
  developer_setup: 'IT Setup',
  provider_added_back: 'Provider Added Back',
  provider_removed: 'Provider Removed'
};

const ACTION_DESCRIPTIONS = {
  assign: 'Choose the owner and status for the next step.',
  updated: 'Update the working status of this ticket.',
  start_work: 'Start active work on the current step.',
  developer_done: 'Move setup to AM for credential validation.',
  credentials_ready: 'Move to AM game launch testing.',
  send_to_qa: 'Assign to QA and mark as QA REVIEWING.',
  report_issue: 'Assign to IT, mark as BLOCKER, and move to IT Fixing.',
  it_fixed: 'Send back to QA for retesting after IT fix.',
  qa_pass: 'Complete the setup ticket after QA passes.',
  qa_fail: 'Assign to IT, mark as BLOCKER, and move to IT Fixing.',
  reopen: 'Reopen the setup.',
  cancel: 'Cancel this setup ticket.'
};

const ACTIONS_BY_STAGE = {
  developer_setup: [
    { key: 'developer_done', requires_assignee: true, variant: 'primary' },
    { key: 'report_issue', requires_assignee: true, requires_note: true, variant: 'secondary' }
  ],
  am_credentials: [
    { key: 'credentials_ready', variant: 'primary' },
    { key: 'report_issue', requires_assignee: true, requires_note: true, variant: 'secondary' }
  ],
  am_testing: [
    { key: 'send_to_qa', requires_assignee: true, variant: 'primary' },
    { key: 'report_issue', requires_assignee: true, requires_note: true, variant: 'secondary' }
  ],
  it_fixing: [
    { key: 'it_fixed', requires_assignee: true, variant: 'primary' }
  ],
  qa_testing: [
    { key: 'qa_pass', requires_close_permission: true, variant: 'primary' },
    { key: 'qa_fail', requires_assignee: true, requires_note: true, variant: 'secondary' }
  ]
};

const COSTING_RATE_TICKET_TYPE = 'costing_rate_change';
const PROVIDER_CHANGE_TICKET_TYPES = ['add_provider', 'change_provider', 'remove_provider'];

const isProviderChangeTicketType = (ticketType) => PROVIDER_CHANGE_TICKET_TYPES.includes(ticketType);

const formatRate = (value) => {
  const rate = Number(value);
  if (!Number.isFinite(rate)) return '-';
  return `${Number.isInteger(rate) ? rate : rate.toFixed(2).replace(/0+$/, '').replace(/\.$/, '')}%`;
};

const getTicketTypeMeta = (ticketType) => {
  if (ticketType === 'record_update') return { label: 'Record change', backgroundColor: 'rgba(var(--primary-rgb), 0.1)', color: 'var(--primary)' };
  if (ticketType === 'l1_create') return { label: 'Add L1 Master Provider', backgroundColor: 'rgba(var(--primary-rgb), 0.1)', color: 'var(--primary)' };
  if (ticketType === 'l0_create') return { label: 'L0 Creation', backgroundColor: 'rgba(var(--primary-rgb), 0.1)', color: 'var(--primary)' };
  if (ticketType === 'add_provider') {
    return {
      label: 'Add Provider',
      backgroundColor: 'rgba(16, 185, 129, 0.1)',
      color: 'var(--success)'
    };
  }
  if (ticketType === 'change_provider') {
    return {
      label: 'Update Provider',
      backgroundColor: 'rgba(var(--primary-rgb), 0.1)',
      color: 'var(--primary)'
    };
  }
  if (ticketType === 'remove_provider') {
    return {
      label: 'Delete Provider',
      backgroundColor: 'rgba(239, 68, 68, 0.1)',
      color: 'var(--error)'
    };
  }
  if (ticketType === COSTING_RATE_TICKET_TYPE) {
    return {
      label: 'L1 Costing',
      backgroundColor: 'rgba(245, 158, 11, 0.14)',
      color: '#b45309'
    };
  }
  if (ticketType === 'manual') {
    return {
      label: 'Manual',
      backgroundColor: 'rgba(var(--primary-rgb), 0.1)',
      color: 'var(--primary)'
    };
  }
  return {
    label: 'Setup',
    backgroundColor: 'rgba(var(--primary-rgb), 0.1)',
    color: 'var(--primary)'
  };
};

const formatLabel = (value) => {
  if (LABEL_OVERRIDES[value]) return LABEL_OVERRIDES[value];
  return String(value || '-').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
};

const formatFileSize = (size = 0) => {
  if (!size) return '0 B';

  const units = ['B', 'KB', 'MB', 'GB'];
  let value = size;
  let index = 0;

  while (value >= 1024 && index < units.length - 1) {
    value /= 1024;
    index += 1;
  }

  return `${value >= 10 || index === 0 ? value.toFixed(0) : value.toFixed(1)} ${units[index]}`;
};

const TicketDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { admin, hasPermission, getDefaultRoute } = useAuth();
  const { closeCurrentTabAndNavigate } = useWorkspaceTabNavigation();

  const canView = hasPermission('tickets', 'view');
  const canEdit = hasPermission('tickets', 'edit');
  const canAssign = hasPermission('tickets', 'assign');
  const canComment = hasPermission('tickets', 'comment');
  const canClose = hasPermission('tickets', 'close');
  const canCreate = hasPermission('tickets', 'create');
  const canApproveCostingRate = ['finance', 'super_admin'].includes(admin?.role) && hasPermission('master_providers', 'edit_financial');
  const canOpenTicket = canView || canCreate || canEdit || canAssign || canComment || canApproveCostingRate || ['finance', 'developer'].includes(admin?.role);
  const canLoadAssignees = canView || canCreate || canAssign;

  const [ticket, setTicket] = useState(null);
  const [editingRequest, setEditingRequest] = useState(false);
  const [assignees, setAssignees] = useState([]);
  const [actionForm, setActionForm] = useState({
    assigned_to_user: '',
    stage: '',
    status: '',
    note: ''
  });
  const [selectedActionKey, setSelectedActionKey] = useState('');
  const [assigneeSearch, setAssigneeSearch] = useState('');
  const [assigneeRoleFilter, setAssigneeRoleFilter] = useState('all');
  const [actionError, setActionError] = useState('');
  const [actionNotice, setActionNotice] = useState(null);
  const [journalPreview, setJournalPreview] = useState({ images: [], index: -1 });
  const [showEditModal, setShowEditModal] = useState(false);
  const [showWorkflowDialog, setShowWorkflowDialog] = useState(false);
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState('');
  const [editForm, setEditForm] = useState({
    title: '',
    description: '',
    merchant_id: '',
    master_provider_id: '',
    priority: 'normal',
    due_date: ''
  });
  const [merchants, setMerchants] = useState([]);
  const [merchantProviders, setMerchantProviders] = useState([]);
  const [loadingMerchants, setLoadingMerchants] = useState(false);
  const [loadingProviders, setLoadingProviders] = useState(false);
  const [editMerchantSearch, setEditMerchantSearch] = useState('');
  const [editProviderSearch, setEditProviderSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [savingAction, setSavingAction] = useState('');
  const [decisionNote, setDecisionNote] = useState('');
  const [decisionError, setDecisionError] = useState('');
  const [decisionNotice, setDecisionNotice] = useState(null);
  const [savingDecision, setSavingDecision] = useState('');
  const journalPreviewImages = journalPreview.images || [];
  const selectedJournalPreview = journalPreview.index >= 0 ? journalPreviewImages[journalPreview.index] : null;

  const handleBackToTickets = useCallback(() => {
    closeCurrentTabAndNavigate('/tickets');
  }, [closeCurrentTabAndNavigate]);

  const openJournalImagePreview = useCallback((images = [], index = 0) => {
    const previewImages = images.map(image => ({
      ...image,
      name: image.original_name || image.file_name || 'Image',
      preview_url: ticketService.getTicketImageUrl(image)
    }));
    setJournalPreview({ images: previewImages, index });
  }, []);

  const moveJournalPreview = useCallback((direction) => {
    setJournalPreview(prev => {
      const images = prev.images || [];
      if (images.length <= 1) return prev;
      return {
        ...prev,
        index: (prev.index + direction + images.length) % images.length
      };
    });
  }, []);

  useEffect(() => {
    if (!selectedJournalPreview || typeof window === 'undefined') return undefined;

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') setJournalPreview({ images: [], index: -1 });
      if (event.key === 'ArrowLeft') moveJournalPreview(-1);
      if (event.key === 'ArrowRight') moveJournalPreview(1);
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [moveJournalPreview, selectedJournalPreview]);

  const fetchTicket = useCallback(async () => {
    if (!canOpenTicket) return;
    setLoading(true);
    const [ticketRes, assigneeRes] = await Promise.all([
      ticketService.getTicketById(id),
      canLoadAssignees ? ticketService.getAssignees() : Promise.resolve({ success: true, data: [] })
    ]);
    if (ticketRes.success) {
      setTicket(ticketRes.data);
      setActionForm({
        assigned_to_user: '',
        stage: ticketRes.data?.stage || '',
        status: ticketRes.data?.status || '',
        note: ''
      });
      setSelectedActionKey('');
      setAssigneeSearch('');
      setAssigneeRoleFilter('all');
      setActionError('');
      setActionNotice(null);
      setDecisionNote('');
      setDecisionError('');
      setDecisionNotice(null);
    } else {
      alert(ticketRes.message || 'Failed to fetch ticket');
      navigate('/tickets');
    }
    if (assigneeRes.success) setAssignees(assigneeRes.data);
    setLoading(false);
  }, [canLoadAssignees, canOpenTicket, id, navigate]);

  useEffect(() => {
    if (!canOpenTicket) {
      navigate(getDefaultRoute(), { replace: true });
      return;
    }
    fetchTicket();
  }, [canOpenTicket, fetchTicket, getDefaultRoute, navigate]);

  const assignedId = ticket?.assigned_to_user?._id || ticket?.assigned_to_user || '';
  const currentAdminId = admin?._id || '';
  const isAssignedToMe = admin?.role === 'super_admin' || (!!assignedId && !!currentAdminId && String(assignedId) === String(currentAdminId));
  const workflow = ticket?.workflow || {};
  const canAssignTicket = workflow.can_assign ?? canAssign;
  const canRunWorkflow = workflow.can_update ?? (canEdit && isAssignedToMe);
  const canWriteWorkflowNote = workflow.can_comment ?? canComment;
  const canCloseTicket = workflow.can_close ?? canClose;
  const canEditDetails = !!workflow.can_edit_details;
  const hasSeparateRequestActions = ['l1_create', COSTING_RATE_TICKET_TYPE].includes(ticket?.ticket_type) ||
    isProviderChangeTicketType(ticket?.ticket_type);

  const stageLabels = useMemo(() => workflow.stage_labels || {}, [workflow.stage_labels]);
  const statusLabels = useMemo(() => workflow.status_labels || {}, [workflow.status_labels]);
  const formatTicketLabel = (value) => stageLabels[value] || statusLabels[value] || formatLabel(value);
  const currentAssigneeLabel = ticket?.assigned_to_user?.username || 'Unassigned';
  const ticketAuthorLabel = getTicketCreatorName(ticket) || 'system';
  const statusOptions = useMemo(() => {
    const configured = workflow.status_options || [];
    if (configured.length > 0) return configured;
    return Object.keys(statusLabels).map(value => ({
      value,
      label: statusLabels[value],
      is_active: true
    }));
  }, [statusLabels, workflow.status_options]);

  const availableActions = useMemo(() => {
    if (!ticket) return [];
    if (['l0_create', 'l1_create', COSTING_RATE_TICKET_TYPE].includes(ticket.ticket_type)) return [];
    if (Array.isArray(ticket.workflow?.available_actions)) {
      return ticket.workflow.available_actions.map(action => ({
        key: action.key,
        label: action.label || ACTION_LABELS[action.key] || formatLabel(action.key),
        description: action.description || ACTION_DESCRIPTIONS[action.key] || '',
        requires_assignee: !!action.requires_assignee,
        requires_note: !!action.requires_note,
        requires_close_permission: !!action.requires_close_permission,
        next_stage: action.next_stage || '',
        next_status: action.next_status || '',
        next_stage_label: action.next_stage_label || '',
        next_status_label: action.next_status_label || '',
        variant: action.variant || 'secondary',
        disabled_reason: action.disabled_reason || ''
      }));
    }
    if (['completed', 'cancelled'].includes(ticket.status)) {
      return [{ key: 'reopen', requires_assignee: true, variant: 'primary' }];
    }

    const stageActions = ACTIONS_BY_STAGE[ticket.stage] || [];
    const actions = ticket.status === 'in_progress'
      ? [...stageActions]
      : [{ key: 'start_work', variant: 'secondary' }, ...stageActions];

    return [...actions, { key: 'cancel', requires_note: true, requires_close_permission: true, variant: 'danger' }];
  }, [ticket]);

  const actionOptions = useMemo(() => {
    const actions = [];
    if (workflow.can_collaborate) {
      if (workflow.can_handoff) actions.push({
        key: 'assign', label: ACTION_LABELS.assign, type: 'collaboration', mode: 'handoff',
        requires_assignee: true, can_select_route: true, description: ACTION_DESCRIPTIONS.assign
      });
      else actions.push({
        key: 'progress', label: 'Update working status', type: 'collaboration', mode: 'progress',
        can_select_route: true, description: 'Update the working status of this request.'
      });
    } else if (!hasSeparateRequestActions) {
      if (canAssignTicket) actions.push({
        key: 'assign', label: ACTION_LABELS.assign, type: 'utility', requires_assignee: true,
        can_select_route: canRunWorkflow && !isRecordCreationTicketType(ticket?.ticket_type),
        description: ACTION_DESCRIPTIONS.assign
      });
      if (canRunWorkflow && !canAssignTicket && !isRecordCreationTicketType(ticket?.ticket_type)) actions.push({
        key: 'updated', label: 'Update status', type: 'utility', can_select_route: true,
        description: 'Update the working status of this ticket.'
      });
      actions.push(...availableActions.map(action => ({
        ...action, type: 'workflow', label: action.label || ACTION_LABELS[action.key] || formatLabel(action.key)
      })));
    }
    return actions;
  }, [availableActions, canAssignTicket, canRunWorkflow, hasSeparateRequestActions,
    ticket?.ticket_type, workflow.can_collaborate, workflow.can_handoff]);

  useEffect(() => {
    if (actionOptions.length === 0) {
      if (selectedActionKey) setSelectedActionKey('');
      return;
    }
    if (actionOptions.some(action => action.key === selectedActionKey)) return;
    const preferredAction = actionOptions.find(action => !action.disabled_reason && action.key === 'assign');
    const firstEnabled = preferredAction || actionOptions.find(action => !action.disabled_reason) || actionOptions[0];
    setSelectedActionKey(firstEnabled.key);
    setActionForm(prev => ({ ...prev, assigned_to_user: firstEnabled.key === 'assign' ? assignedId : '', note: '' }));
    setAssigneeSearch(firstEnabled.key === 'assign' && assignedId ? currentAssigneeLabel : '');
  }, [actionOptions, assignedId, currentAssigneeLabel, selectedActionKey]);

  const selectedAction = actionOptions.find(action => action.key === selectedActionKey) || null;
  const selectedActionNeedsAssignee = selectedAction?.requires_assignee || selectedAction?.key === 'assign';
  const selectedActionNeedsNote = !!selectedAction?.requires_note;
  const selectedActionNeedsClosePermission = !!selectedAction?.requires_close_permission;
  const selectedActionCanSelectRoute = !!selectedAction?.can_select_route;
  const selectedActionNextStage = selectedAction?.next_stage || '';
  const selectedActionNextStatus = selectedAction?.next_status || '';
  const showAssigneeField = !!selectedAction && selectedActionNeedsAssignee;
  const showRouteFields = !!selectedAction;
  const selectedAssignee = assignees.find(user => user._id === actionForm.assigned_to_user) || null;
  const roleOptions = useMemo(() => (
    [...new Set(assignees.map(user => user.role).filter(Boolean))].sort((a, b) => a.localeCompare(b))
  ), [assignees]);
  const filteredAssignees = useMemo(() => {
    const search = assigneeSearch.trim().toLowerCase();
    return assignees.filter(user => {
      const roleMatches = assigneeRoleFilter === 'all' || user.role === assigneeRoleFilter;
      const text = `${user.username || ''} ${user.email || ''} ${user.role || ''}`.toLowerCase();
      return roleMatches && (!search || text.includes(search));
    });
  }, [assigneeRoleFilter, assigneeSearch, assignees]);
  const previousOwners = [...new Set((ticket?.activity_logs || []).flatMap(entry => [
    entry.from?.assigned_to_user?._id || entry.from?.assigned_to_user,
    entry.to?.assigned_to_user?._id || entry.to?.assigned_to_user
  ]).reverse().filter(Boolean).map(String))]
    .filter(ownerId => ownerId !== String(assignedId))
    .map(ownerId => assignees.find(user => String(user._id) === ownerId)).filter(Boolean).slice(0, 5);
  const canAssignToMe = !!admin?._id && assignees.some(user => user._id === admin._id);

  const getMerchantLabel = useCallback((merchant) => (
    [merchant?.name, merchant?.merchant_code || merchant?.client_code].filter(Boolean).join(' / ') || 'Merchant'
  ), []);
  const getProviderItem = useCallback((item) => item?.provider_id || {}, []);
  const getProviderLabel = useCallback((item) => {
    const provider = getProviderItem(item);
    return provider.provider_id?.name || provider.code || 'Provider';
  }, [getProviderItem]);
  const getMasterProviderLabel = useCallback((provider) => (
    [
      provider?.provider_id?.name || provider?.provider_id?.code || provider?.code,
      provider?.type,
      provider?.source?.name,
      getProviderReferenceLabel(provider?.currency, true),
      getProviderReferenceLabel(provider?.country)
    ].filter(Boolean).join(' / ') ||
    'L1 Provider'
  ), []);
  const getAssigneeLabel = useCallback((user) => user?.username || user?.email || 'Assignee', []);
  const formatDateInput = (value) => {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    return date.toISOString().slice(0, 10);
  };
  const selectedEditMerchant = useMemo(
    () => merchants.find(merchant => merchant._id === editForm.merchant_id) || null,
    [merchants, editForm.merchant_id]
  );
  const selectedEditProvider = useMemo(
    () => merchantProviders.find(item => getProviderItem(item)._id === editForm.master_provider_id) || null,
    [merchantProviders, editForm.master_provider_id, getProviderItem]
  );
  const filteredEditMerchants = useMemo(() => {
    const query = editMerchantSearch.trim().toLowerCase();
    if (!query) return merchants;
    return merchants.filter(merchant => (
      getMerchantLabel(merchant).toLowerCase().includes(query) ||
      String(merchant.merchant_code || '').toLowerCase().includes(query) ||
      String(merchant.client_code || '').toLowerCase().includes(query)
    ));
  }, [editMerchantSearch, getMerchantLabel, merchants]);
  const filteredEditProviders = useMemo(() => {
    const query = editProviderSearch.trim().toLowerCase();
    if (!query) return merchantProviders;
    return merchantProviders.filter(item => {
      const provider = getProviderItem(item);
      return (
        getProviderLabel(item).toLowerCase().includes(query) ||
        String(provider.code || '').toLowerCase().includes(query) ||
        String(provider.type || '').toLowerCase().includes(query)
      );
    });
  }, [editProviderSearch, getProviderItem, getProviderLabel, merchantProviders]);
  const editMerchantSearchMatchesSelection = editForm.merchant_id && editMerchantSearch.trim().toLowerCase() === getMerchantLabel(selectedEditMerchant || ticket?.merchant_id).toLowerCase();
  const editProviderSearchMatchesSelection = editForm.master_provider_id && editProviderSearch.trim().toLowerCase() === (selectedEditProvider ? getProviderLabel(selectedEditProvider) : (ticket?.master_provider_id?.provider_id?.name || ticket?.master_provider_id?.code || 'Provider')).toLowerCase();

  useEffect(() => {
    if (!showEditModal || ticket?.ticket_type !== 'manual' || !hasPermission('merchants', 'view') || merchants.length > 0) return;
    setLoadingMerchants(true);
    merchantService.getMerchants({ page: 1, limit: 1000 }).then(res => {
      if (res.success) setMerchants(res.data || []);
    }).finally(() => setLoadingMerchants(false));
  }, [showEditModal, merchants.length, ticket?.ticket_type, hasPermission]);

  useEffect(() => {
    if (!showEditModal || ticket?.ticket_type !== 'manual' || !hasPermission('merchants', 'view') || !editForm.merchant_id) {
      setMerchantProviders([]);
      return;
    }
    setLoadingProviders(true);
    merchantService.getMerchantProviders(editForm.merchant_id, { page: 1, limit: 1000 }).then(res => {
      if (res.success) {
        setMerchantProviders(res.data || []);
        return;
      }
      setMerchantProviders([]);
    }).finally(() => setLoadingProviders(false));
  }, [showEditModal, editForm.merchant_id, ticket?.ticket_type, hasPermission]);

  const openEditModal = () => {
    if (!ticket) return;
    const merchantLabel = ticket.merchant_id ? getMerchantLabel(ticket.merchant_id) : '';
    const providerLabel = ticket.master_provider_id
      ? (ticket.master_provider_id.provider_id?.name || ticket.master_provider_id.code || 'Provider')
      : '';
    setEditForm({
      title: ticket.title || '',
      description: ticket.description || '',
      merchant_id: ticket.merchant_id?._id || '',
      master_provider_id: ticket.master_provider_id?._id || '',
      priority: ticket.priority || 'normal',
      due_date: formatDateInput(ticket.due_date),
      expected_revision: ticket.request_revision || 0
    });
    setEditMerchantSearch(merchantLabel);
    setEditProviderSearch(providerLabel);
    setEditError('');
    setShowEditModal(true);
  };

  const updateEditForm = (field, value) => {
    setEditError('');
    setEditForm(prev => ({ ...prev, [field]: value }));
  };

  const updateEditMerchantSearch = (value) => {
    setEditError('');
    setEditMerchantSearch(value);
    if (editForm.merchant_id && value.trim().toLowerCase() !== getMerchantLabel(selectedEditMerchant).toLowerCase()) {
      setEditForm(prev => ({ ...prev, merchant_id: '', master_provider_id: '' }));
      setEditProviderSearch('');
    }
  };

  const updateEditProviderSearch = (value) => {
    setEditError('');
    setEditProviderSearch(value);
    if (editForm.master_provider_id && value.trim().toLowerCase() !== getProviderLabel(selectedEditProvider).toLowerCase()) {
      setEditForm(prev => ({ ...prev, master_provider_id: '' }));
    }
  };

  const selectEditMerchant = (merchant) => {
    setEditError('');
    setEditMerchantSearch(getMerchantLabel(merchant));
    setEditProviderSearch('');
    setEditForm(prev => ({ ...prev, merchant_id: merchant._id, master_provider_id: '' }));
  };

  const selectEditProvider = (item) => {
    const provider = getProviderItem(item);
    setEditError('');
    setEditProviderSearch(getProviderLabel(item));
    setEditForm(prev => ({ ...prev, master_provider_id: provider._id }));
  };

  const handleEditTicket = async () => {
    if (!canEditDetails) return;
    if (!editForm.title.trim()) {
      setEditError('Ticket name is required.');
      return;
    }
    setEditSaving(true);
    setEditError('');
    const payload = {
      ...editForm,
      title: editForm.title.trim(),
      description: editForm.description.trim(),
      merchant_id: isSetupTicket ? ticket.merchant_id?._id || null : editForm.merchant_id || null,
      master_provider_id: isSetupTicket ? ticket.master_provider_id?._id || null : editForm.master_provider_id || null,
      due_date: editForm.due_date || null
    };
    const res = await ticketService.updateTicket(ticket._id, payload);
    setEditSaving(false);
    if (res.success) {
      setTicket(res.data);
      setShowEditModal(false);
      setActionNotice({ type: 'success', message: 'Ticket details saved.' });
      return;
    }
    setEditError(res.message || 'Failed to save ticket.');
  };

  const selectAssignee = (assigneeId) => {
    setActionError('');
    setActionNotice(null);
    const user = assignees.find(item => item._id === assigneeId);
    setAssigneeSearch(user ? (user.username || user.email || '') : '');
    setActionForm(prev => ({ ...prev, assigned_to_user: assigneeId }));
  };

  const handleAssigneeSearchChange = (value) => {
    setAssigneeSearch(value);
    setActionError('');
    setActionNotice(null);
    if (!actionForm.assigned_to_user) return;

    const searchValue = value.trim().toLowerCase();
    const selectedAssigneeLabel = getAssigneeLabel(selectedAssignee).toLowerCase();
    const selectedAssigneeEmail = String(selectedAssignee?.email || '').toLowerCase();

    if (!searchValue || (searchValue !== selectedAssigneeLabel && searchValue !== selectedAssigneeEmail)) {
      setActionForm(prev => ({ ...prev, assigned_to_user: '' }));
    }
  };

  useEffect(() => {
    if (!ticket || !selectedActionKey) return;
    setActionForm(prev => ({
      ...prev,
      stage: selectedActionCanSelectRoute
        ? (prev.stage || ticket.stage || '')
        : (selectedActionNextStage || ticket.stage || ''),
      status: selectedActionCanSelectRoute
        ? (prev.status || ticket.status || '')
        : (selectedActionNextStatus || ticket.status || '')
    }));
  }, [
    selectedActionCanSelectRoute,
    selectedActionKey,
    selectedActionNextStage,
    selectedActionNextStatus,
    ticket,
    ticket?.stage,
    ticket?.status
  ]);

  const formatJournalValue = (field, value) => {
    if (!value) return 'Unassigned';
    if (field === 'assigned_to_user') {
      const userId = value?._id || value;
      const user = assignees.find(item => item._id === String(userId));
      return user ? user.username : String(userId);
    }
    if (field === 'due_date') {
      const date = new Date(value);
      return Number.isNaN(date.getTime()) ? '-' : date.toLocaleDateString();
    }
    if (field === 'merchant_id') {
      const merchantId = value?._id || value;
      if (ticket?.merchant_id?._id && String(ticket.merchant_id._id) === String(merchantId)) {
        return ticket.merchant_id.merchant_code || ticket.merchant_id.name || 'Linked merchant';
      }
      return value ? 'Linked merchant' : 'No merchant';
    }
    if (field === 'master_provider_id') {
      const providerId = value?._id || value;
      if (ticket?.master_provider_id?._id && String(ticket.master_provider_id._id) === String(providerId)) {
        return ticket.master_provider_id.provider_id?.name || ticket.master_provider_id.code || 'Linked provider';
      }
      return value ? 'Linked provider' : 'No provider';
    }
    if (field === 'description') return value ? String(value) : 'No description';
    if (field === 'title') return String(value || '-');
    if (field === 'priority') return formatLabel(value);
    return formatTicketLabel(value);
  };

  const hasJournalFieldChange = (entry, field) => {
    const from = entry.from?.[field];
    const to = entry.to?.[field];
    return to !== undefined && String(from || '') !== String(to || '');
  };

  const getJournalChanges = (entry) => {
    if (entry.action === 'created' && entry.to?.ticket_type === COSTING_RATE_TICKET_TYPE) {
      return [{
        label: 'Requested Rate',
        from: formatRate(entry.to?.costing_rate_change?.old_rate),
        to: formatRate(entry.to?.costing_rate_change?.requested_rate)
      }];
    }
    if (entry.action === 'costing_rate_approved') {
      return [{
        label: 'Costing Rate',
        from: formatRate(entry.from?.current_provider_rate ?? entry.from?.costing_rate_change?.old_rate),
        to: formatRate(entry.to?.current_provider_rate ?? entry.to?.costing_rate_change?.requested_rate)
      }];
    }
    if (entry.action === 'costing_rate_rejected') {
      return [{
        label: 'Decision',
        text: 'Request rejected'
      }];
    }
    if (entry.action === 'created' && isProviderChangeTicketType(entry.to?.ticket_type)) {
      return [{
        label: 'Request',
        text: getTicketTypeMeta(entry.to.ticket_type).label
      }];
    }
    if (entry.action === 'provider_change_approved') return [{ label: 'Decision', text: 'Request approved' }];
    if (entry.action === 'provider_change_it_approved') return [{ label: 'IT Approval', text: 'Approved' }];
    if (entry.action === 'provider_change_finance_approved') return [{ label: 'Finance Approval', text: 'Approved' }];
    if (entry.action === 'provider_change_rejected') return [{ label: 'Decision', text: 'Request rejected' }];
    if (entry.action === 'provider_change_submitted') return [{ label: 'Provider Config', text: 'Merchant provider configuration updated' }];
    if (entry.action === 'created') return [{ label: 'Event', text: 'Ticket created' }];
    if (entry.action === 'commented') return [{ label: 'Comment', text: entry.note || 'Comment added' }];

    const changes = [];
    if (entry.action && !['assign', 'updated', 'cancelled'].includes(entry.action)) {
      changes.push({ label: 'Action', text: entry.action_label || workflow.action_labels?.[entry.action] || ACTION_LABELS[entry.action] || formatLabel(entry.action), isAction: true });
    }
    ['title', 'description', 'merchant_id', 'master_provider_id', 'status', 'assigned_to_user', 'priority', 'due_date'].forEach(field => {
      const from = entry.from?.[field];
      const to = entry.to?.[field];
      if (to !== undefined && String(from || '') !== String(to || '')) {
        changes.push({
          label: formatLabel(field),
          field,
          from: formatJournalValue(field, from),
          to: formatJournalValue(field, to)
        });
      }
    });
    return changes.length > 0 ? changes : [{ label: 'Event', text: ACTION_LABELS[entry.action] || formatLabel(entry.action) }];
  };

  const getJournalTitle = (entry) => {
    const actorName = entry.actor_username || 'system';
    const withActor = (text) => ({ actorName, text });
    if (entry.action === 'created' && entry.to?.ticket_type === COSTING_RATE_TICKET_TYPE) return withActor('requested a costing rate change');
    if (entry.action === 'created' && isProviderChangeTicketType(entry.to?.ticket_type)) return withActor(`requested ${getTicketTypeMeta(entry.to.ticket_type).label.toLowerCase()}`);
    if (entry.action === 'created') return withActor('created this ticket');
    if (entry.action === 'commented') return withActor('added a comment');
    if (entry.action === 'assign' || hasJournalFieldChange(entry, 'assigned_to_user')) {
      const assignee = formatJournalValue('assigned_to_user', entry.to?.assigned_to_user);
      return withActor(assignee === 'Unassigned' ? 'unassigned this ticket' : `assigned this ticket to ${assignee}`);
    }
    if (entry.action === 'updated') return withActor('updated assignment');
    if (entry.action === 'details_updated') return withActor('edited ticket details');
    if (entry.action === 'request_updated') return withActor('edited the request');
    if (entry.action === 'request_handoff') return withActor('handed off this request');
    if (entry.action === 'request_returned') return withActor('returned this request for corrections');
    if (entry.action === 'request_review') return withActor('requested final review');
    if (entry.action === 'request_progress') return withActor('updated working progress');
    if (entry.action === 'costing_rate_approved') return withActor('approved this costing rate change');
    if (entry.action === 'costing_rate_rejected') return withActor('rejected this costing rate change');
    if (entry.action === 'provider_change_approved') return withActor('approved this provider change');
    if (entry.action === 'provider_change_it_approved') return withActor('approved this provider change for IT');
    if (entry.action === 'provider_change_finance_approved') return withActor('approved this provider change for Finance');
    if (entry.action === 'provider_change_rejected') return withActor('rejected this provider change');
    if (entry.action === 'provider_change_submitted') return withActor('applied this provider change');
    if (entry.action === 'provider_removed') return withActor('marked provider removed');
    if (entry.action === 'provider_added_back') return withActor('restored provider setup');
    const label = entry.action_label || workflow.action_labels?.[entry.action] || ACTION_LABELS[entry.action] || formatLabel(entry.action);
    return withActor(`ran ${label}`);
  };

  const handleAction = async (action) => {
    if (!ticket || savingAction || editingRequest) return;
    setActionError('');
    setActionNotice(null);
    if (action.key === 'assign' && !(action.type === 'collaboration' ? workflow.can_handoff : canAssignTicket)) {
      setActionError('You do not have permission to assign this ticket.');
      return;
    }
    if (action.type === 'collaboration' ? !workflow.can_collaborate : action.key !== 'assign' && !canRunWorkflow) {
      setActionError('Only the current assignee can update this ticket. Reassign it first when ownership changes.');
      return;
    }
    if (action.requires_close_permission && !canCloseTicket) {
      setActionError('Close permission is required for this action.');
      return;
    }
    if ((action.requires_assignee || action.key === 'assign') && !actionForm.assigned_to_user) {
      setActionError('Select an assignee before continuing.');
      return;
    }
    if (action.requires_note && !actionForm.note.trim()) {
      setActionError('Add a reason before continuing.');
      return;
    }
    if (action.requires_note && actionForm.note.trim() && !canWriteWorkflowNote) {
      setActionError('Comment permission is required to add a workflow reason.');
      return;
    }
    if (action.can_select_route && (!actionForm.stage || !actionForm.status)) {
      setActionError('Select status before continuing.');
      return;
    }

    const payload = buildTicketActionPayload(ticket, action, actionForm);

    setSavingAction(action.key);
    const res = await (action.type === 'collaboration'
      ? ticketService.updateCollaboration(ticket._id, payload)
      : ticketService.performAction(ticket._id, payload));
    setSavingAction('');

    if (res.success) {
      setTicket(res.data);
      setShowWorkflowDialog(false);
      setSelectedActionKey('');
      setActionForm({
        assigned_to_user: '',
        stage: res.data?.stage || '',
        status: res.data?.status || '',
        note: ''
      });
      setAssigneeSearch('');
      setAssigneeRoleFilter('all');
      setActionNotice({
        type: 'success',
        message: action.key === 'assign' ? 'Assignment updated.' : `${action.label || ACTION_LABELS[action.key] || 'Action'} saved.`
      });
    } else {
      setActionError(res.message || 'Failed to update ticket');
    }
  };

  const handleCostingRateDecision = async (decision) => {
    if (!ticket) return;
    const note = decisionNote.trim();
    setDecisionError('');
    setDecisionNotice(null);

    if (decision === 'reject' && !note) {
      setDecisionError('A rejection note is required.');
      return;
    }

    setSavingDecision(decision);
    const res = await ticketService.decideCostingRateChange(ticket._id, { decision, note });
    setSavingDecision('');

    if (res.success) {
      setTicket(res.data);
      setDecisionNote('');
      setDecisionNotice({
        type: 'success',
        message: decision === 'approve' ? 'Costing rate approved and updated.' : 'Costing rate change rejected.'
      });
      return;
    }

    setDecisionError(res.message || 'Failed to update costing rate decision.');
  };

  const handleProviderChangeDecision = async (decision) => {
    if (editingRequest) return;
    if (!ticket) return;
    const note = decisionNote.trim();
    setDecisionError('');
    setDecisionNotice(null);

    if (decision === 'reject' && !note) {
      setDecisionError('A rejection note is required.');
      return;
    }

    setSavingDecision(decision);
    const res = await ticketService.decideProviderChange(ticket._id, { decision, note, expected_revision: ticket.request_revision || 0 });
    setSavingDecision('');

    if (res.success) {
      setTicket(res.data);
      setDecisionNote('');
      if (decision === 'approve') notifyMerchantUpdated();
      setDecisionNotice({
        type: 'success',
        message: decision === 'approve'
          ? 'Provider change approved, applied, and completed.'
          : 'Provider change rejected and cancelled.'
      });
      return;
    }

    const refreshed = await ticketService.getTicketById(ticket._id);
    if (refreshed.success) setTicket(refreshed.data);
    setDecisionError(res.message || 'Failed to update provider change decision.');
  };

  if (loading) {
    return <div style={{ padding: '2rem', textAlign: 'center' }}>Loading ticket...</div>;
  }

  if (!ticket) {
    return <div style={{ padding: '2rem', textAlign: 'center' }}>Ticket not found.</div>;
  }

  const isSetupTicket = (ticket.ticket_type || 'setup') === 'setup';
  const isRecordCreationTicket = isRecordCreationTicketType(ticket.ticket_type);
  const isCostingRateTicket = ticket.ticket_type === COSTING_RATE_TICKET_TYPE;
  const isProviderChangeTicket = isProviderChangeTicketType(ticket.ticket_type);
  const isCollaborativeProviderTicket = isCollaborativeProviderRequest(ticket);
  const isProviderRequestPreparing = isCollaborativeProviderTicket && ticket.provider_change?.status === 'pending' && ticket.stage !== 'finance_approval' && !['completed', 'cancelled'].includes(ticket.status);
  const typeMeta = getTicketTypeMeta(ticket.ticket_type);
  const costingRateChange = ticket.costing_rate_change || {};
  const canApproveThisCostingRate = !!workflow.can_approve_costing_rate_change;
  const canRejectThisCostingRate = !!workflow.can_reject_costing_rate_change;
  const costingDecisionStatus = costingRateChange.status || 'pending';
  const costingRateStatusLabel = workflow.current_status_label || formatTicketLabel(costingDecisionStatus === 'pending' ? 'pending_approval' : costingDecisionStatus);
  const providerChange = ticket.provider_change || {};
  const providerChangeAction = providerChange.action || ticket.ticket_type;
  const providerChangeConfig = providerChange.provider_config || {};
  const providerChangeStatus = providerChange.status || 'pending';
  const providerChangeStatusLabel = workflow.current_status_label || formatTicketLabel(({ pending: 'pending_approval', submitted: 'completed' })[providerChangeStatus] || providerChangeStatus);
  const canApproveThisProviderChange = !!workflow.can_approve_provider_change;
  const canRejectThisProviderChange = !!workflow.can_reject_provider_change;
  const providerChangeFromLabel = providerChange.from_master_provider_id
    ? `${getMasterProviderLabel(providerChange.from_master_provider_id)} · ${getProviderCategoryContext(providerChange.from_master_provider_id)}` : '-';
  const providerChangeToLabel = providerChange.to_master_provider_id
    ? `${getMasterProviderLabel(providerChange.to_master_provider_id)} · ${getProviderCategoryContext(providerChange.to_master_provider_id)}` : '-';
  const providerChangeHasConfig = ['add_provider', 'change_provider'].includes(providerChangeAction);
  const selectedNeedsClosePermission = selectedActionNeedsClosePermission;
  const selectedActionDisabledReason = selectedAction?.disabled_reason || '';
  const routeStatusValue = selectedActionCanSelectRoute
    ? (actionForm.status || ticket.status)
    : (selectedActionNextStatus || ticket.status);
  const previewStatusLabel = formatTicketLabel(routeStatusValue);
  const previewAssigneeLabel = selectedActionNeedsAssignee
    ? (selectedAssignee?.username || 'Select assignee')
    : selectedAssignee?.username
      ? selectedAssignee.username
    : currentAssigneeLabel;
  const assigneeHelperText = selectedAction?.key === 'assign'
    ? `Current owner: ${currentAssigneeLabel}`
    : 'Choose who owns the next step after this action.';
  const submitDisabled = !selectedAction ||
    editingRequest ||
    !!savingAction ||
    !!selectedActionDisabledReason ||
    (selectedAction.type === 'workflow' && !canRunWorkflow) ||
    (selectedNeedsClosePermission && !canCloseTicket) ||
    (selectedActionNeedsAssignee && !actionForm.assigned_to_user) ||
    (selectedActionCanSelectRoute && (!actionForm.stage || !actionForm.status)) ||
    (selectedActionNeedsNote && !actionForm.note.trim());
  const submitLabel = selectedAction?.key === 'assign' ? 'Save assignment' : selectedAction?.label || 'Save';
  const workflowDialogTitle = actionOptions.some(action => action.key === 'assign') ? 'Assign / hand off' : 'Update status';


  return (
    <div className="ticket-detail-page">
      <div className="ticket-detail-shell">
        <div className="ticket-workspace">
        <div className="ticket-detail-main">
          <div className="ticket-back-row">
            <button className="ticket-back-link" onClick={handleBackToTickets}>
              <ArrowLeftIcon /><span>Tickets</span>
            </button>
          </div>

          <TicketSummary ticket={ticket}>
            {canEditDetails && (
              <button className="btn btn-secondary" onClick={openEditModal}>Edit ticket</button>
            )}
            {actionOptions.length > 0 && <button type="button" className="btn btn-primary" disabled={!!savingAction || editingRequest}
              title={editingRequest ? 'Save or cancel configuration edits first.' : undefined}
              onClick={() => {
                const action = actionOptions.find(option => option.key === 'assign') || actionOptions[0];
                setSelectedActionKey(action.key);
                setActionForm({ assigned_to_user: action.key === 'assign' ? assignedId : '', stage: ticket.stage, status: ticket.status, note: '' });
                setAssigneeSearch(action.key === 'assign' && assignedId ? currentAssigneeLabel : '');
                setAssigneeRoleFilter('all');
                setActionError('');
                setActionNotice(null);
                setShowWorkflowDialog(true);
              }}>{workflowDialogTitle}</button>}
          </TicketSummary>
          {actionNotice?.type === 'success' && <p role="status" className="ticket-page-feedback">{actionNotice.message}</p>}
          {isCostingRateTicket && (
            <div className="card" style={{ padding: '1.25rem', marginBottom: '1rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem', alignItems: 'flex-start', flexWrap: 'wrap', marginBottom: '1rem' }}>
                <div>
                  <h3 style={{ margin: 0, color: 'var(--text-primary)', fontSize: '1.05rem', fontWeight: 900 }}>Costing Rate Request</h3>
                  <div style={{ marginTop: '0.25rem', color: 'var(--text-secondary)', fontSize: '0.84rem' }}>{getMasterProviderLabel(ticket.master_provider_id)}</div>
                </div>
                <span style={{ display: 'inline-flex', alignItems: 'center', minHeight: '30px', padding: '0.25rem 0.7rem', borderRadius: '999px', backgroundColor: typeMeta.backgroundColor, color: typeMeta.color, fontSize: '0.78rem', fontWeight: 900 }}>{costingRateStatusLabel}</span>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '0.75rem' }}>
                <div style={{ border: '1px solid var(--border-light)', borderRadius: '8px', padding: '0.85rem', backgroundColor: 'var(--bg-soft)' }}>
                  <div style={{ fontSize: '0.72rem', fontWeight: 900, color: 'var(--text-secondary)', textTransform: 'uppercase', marginBottom: '0.35rem' }}>Current Rate</div>
                  <div style={{ color: 'var(--text-primary)', fontSize: '1.1rem', fontWeight: 900 }}>{formatRate(costingRateChange.old_rate)}</div>
                </div>
                <div style={{ border: '1px solid var(--border-light)', borderRadius: '8px', padding: '0.85rem', backgroundColor: 'var(--bg-soft)' }}>
                  <div style={{ fontSize: '0.72rem', fontWeight: 900, color: 'var(--text-secondary)', textTransform: 'uppercase', marginBottom: '0.35rem' }}>Requested Rate</div>
                  <div style={{ color: 'var(--text-primary)', fontSize: '1.1rem', fontWeight: 900 }}>{formatRate(costingRateChange.requested_rate)}</div>
                </div>
                <div style={{ border: '1px solid var(--border-light)', borderRadius: '8px', padding: '0.85rem', backgroundColor: 'var(--bg-soft)' }}>
                  <div style={{ fontSize: '0.72rem', fontWeight: 900, color: 'var(--text-secondary)', textTransform: 'uppercase', marginBottom: '0.35rem' }}>Requester</div>
                  <div style={{ color: 'var(--text-primary)', fontSize: '0.95rem', fontWeight: 900 }}>{costingRateChange.requested_by?.username || costingRateChange.requested_by_username || ticketAuthorLabel}</div>
                </div>
                <div style={{ border: '1px solid var(--border-light)', borderRadius: '8px', padding: '0.85rem', backgroundColor: 'var(--bg-soft)' }}>
                  <div style={{ fontSize: '0.72rem', fontWeight: 900, color: 'var(--text-secondary)', textTransform: 'uppercase', marginBottom: '0.35rem' }}>Decision</div>
                  <div style={{ color: 'var(--text-primary)', fontSize: '0.95rem', fontWeight: 900 }}>
                    {costingRateChange.decided_by?.username || costingRateChange.decided_by_username || '-'}
                  </div>
                </div>
              </div>
            </div>
          )}

          {ticket.ticket_type === 'record_update' && <RecordUpdatePanel key={ticket._id} ticket={ticket} onUpdate={setTicket} onEditingChange={setEditingRequest} />}
          {isRecordCreationTicket && <RecordCreationPanel key={ticket._id} ticket={ticket} onUpdate={setTicket} onEditingChange={setEditingRequest} />}
          {isProviderChangeTicket && (
            <section className="card ticket-content-card">
              <TicketRequestEditor key={`request-${ticket._id}`} ticket={ticket} onUpdate={setTicket} onEditingChange={setEditingRequest}>
              {!isCollaborativeProviderTicket && <div className="ticket-section-heading"><h3>{providerChangeHasConfig ? 'Provider configuration' : 'Provider removal'}</h3></div>}
              {!isCollaborativeProviderTicket && <TicketFieldList fields={[
                { label: 'Current provider', value: providerChangeFromLabel },
                ...(providerChangeHasConfig ? [{ label: 'Target provider', value: providerChangeToLabel }] : [])
              ]} />}
              {providerChangeHasConfig && <ProviderConfigSummary config={providerChangeConfig} provider={getTicketProviderDefinition(ticket.provider_change.to_master_provider_id || ticket.provider_change.from_master_provider_id, isPaymentGatewayTicket(ticket))} />}
              </TicketRequestEditor>
            </section>
          )}

          {isCostingRateTicket && (
            <div className="card" style={{ padding: '1.25rem', marginBottom: '1rem' }}>
              <div style={{ marginBottom: '0.9rem' }}>
                <h3 style={{ margin: 0, color: 'var(--text-primary)', fontSize: '1.05rem', fontWeight: 900 }}>Approval</h3>
                <p style={{ margin: '0.25rem 0 0 0', color: 'var(--text-secondary)', fontSize: '0.84rem', lineHeight: 1.4 }}>
                  {costingDecisionStatus === 'pending'
                    ? 'Approve to update the L1 master costing rate immediately, or reject with a note.'
                    : 'This costing rate change has already been decided.'}
                </p>
              </div>

              {decisionNotice && (
                <div style={{ marginBottom: '1rem', padding: '0.85rem 1rem', borderRadius: '8px', border: '1px solid rgba(16, 185, 129, 0.35)', backgroundColor: 'rgba(16, 185, 129, 0.08)', color: 'var(--success)', fontSize: '0.875rem', fontWeight: 700 }}>
                  {decisionNotice.message}
                </div>
              )}
              {decisionError && (
                <div style={{ marginBottom: '1rem', padding: '0.85rem 1rem', borderRadius: '8px', border: '1px solid rgba(239, 68, 68, 0.35)', backgroundColor: 'rgba(239, 68, 68, 0.08)', color: 'var(--error)', fontSize: '0.875rem', fontWeight: 700 }}>
                  {decisionError}
                </div>
              )}

              {costingDecisionStatus === 'pending' && (canApproveThisCostingRate || canRejectThisCostingRate) ? (
                <>
                  <div style={{ marginBottom: '0.9rem' }}>
                    <label style={{ display: 'block', marginBottom: '0.4rem', fontSize: '0.78rem', fontWeight: 800, color: 'var(--text-secondary)', textTransform: 'uppercase' }}>Decision Note</label>
                    <textarea className="input" rows="4" value={decisionNote} onChange={(e) => { setDecisionNote(e.target.value); setDecisionError(''); setDecisionNotice(null); }} placeholder="Required when rejecting..." disabled={!!savingDecision} />
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem', flexWrap: 'wrap' }}>
                    <button type="button" className="btn btn-secondary" disabled={!!savingDecision} onClick={() => handleCostingRateDecision('reject')}>
                      {savingDecision === 'reject' ? 'Rejecting...' : 'Reject'}
                    </button>
                    <button type="button" className="btn btn-primary" disabled={!!savingDecision} onClick={() => handleCostingRateDecision('approve')}>
                      {savingDecision === 'approve' ? 'Approving...' : 'Approve'}
                    </button>
                  </div>
                </>
              ) : (
                <div style={{ padding: '0.85rem 1rem', borderRadius: '8px', border: '1px solid var(--border-light)', backgroundColor: 'var(--bg-soft)', color: 'var(--text-secondary)', fontSize: '0.875rem', fontWeight: 700 }}>
                  {costingDecisionStatus === 'pending' ? 'Waiting for a financial approver.' : `Decision: ${costingRateStatusLabel}`}
                </div>
              )}
            </div>
          )}

          {isProviderChangeTicket && (!isCollaborativeProviderTicket || !isProviderRequestPreparing) && (!isCollaborativeProviderTicket || ticket.status !== 'cancelled' || providerChangeStatus === 'rejected') && (
            <div className="card ticket-content-card ticket-approval-card">
              <div style={{ marginBottom: '0.9rem' }}>
                <h3 style={{ margin: 0, color: 'var(--text-primary)', fontSize: '1.05rem', fontWeight: 900 }}>Approval</h3>
                <p style={{ margin: '0.25rem 0 0 0', color: 'var(--text-secondary)', fontSize: '0.84rem', lineHeight: 1.4 }}>
                  {providerChangeStatus === 'pending'
                    ? 'Finance or Superadmin approval applies the change and completes the ticket.'
                    : providerChangeStatus === 'approved'
                      ? 'Approval is saved. Apply the approved change to complete this ticket.'
                      : `Decision: ${providerChangeStatusLabel}`}
                </p>
              </div>

              {decisionNotice && (
                <div style={{ marginBottom: '1rem', padding: '0.85rem 1rem', borderRadius: '8px', border: '1px solid rgba(16, 185, 129, 0.35)', backgroundColor: 'rgba(16, 185, 129, 0.08)', color: 'var(--success)', fontSize: '0.875rem', fontWeight: 700 }}>
                  {decisionNotice.message}
                </div>
              )}
              {decisionError && (
                <div style={{ marginBottom: '1rem', padding: '0.85rem 1rem', borderRadius: '8px', border: '1px solid rgba(239, 68, 68, 0.35)', backgroundColor: 'rgba(239, 68, 68, 0.08)', color: 'var(--error)', fontSize: '0.875rem', fontWeight: 700 }}>
                  {decisionError}
                </div>
              )}

              {editingRequest && <p className="ticket-muted">Save or cancel configuration edits before making a decision.</p>}
              {['pending', 'approved'].includes(providerChangeStatus) && (canApproveThisProviderChange || canRejectThisProviderChange) ? (
                <>
                  <div style={{ marginBottom: '0.9rem' }}>
                    <label style={{ display: 'block', marginBottom: '0.4rem', fontSize: '0.78rem', fontWeight: 800, color: 'var(--text-secondary)', textTransform: 'uppercase' }}>Decision Note</label>
                    <textarea className="input" rows="4" value={decisionNote} onChange={(e) => { setDecisionNote(e.target.value); setDecisionError(''); setDecisionNotice(null); }} placeholder="Required when rejecting..." disabled={editingRequest || !!savingDecision} />
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem', flexWrap: 'wrap' }}>
                    {canRejectThisProviderChange && <button type="button" className="btn btn-secondary" disabled={editingRequest || !!savingDecision} onClick={() => handleProviderChangeDecision('reject')}>
                      {savingDecision === 'reject' ? 'Rejecting...' : 'Reject'}
                    </button>}
                    <button type="button" className="btn btn-primary" disabled={editingRequest || !!savingDecision || !canApproveThisProviderChange} onClick={() => handleProviderChangeDecision('approve')}>
                      {savingDecision === 'approve' ? 'Applying...' : providerChangeStatus === 'approved' ? 'Apply Approved Change' : 'Approve'}
                    </button>
                  </div>
                </>
              ) : (
                <div style={{ padding: '0.85rem 1rem', borderRadius: '8px', border: '1px solid var(--border-light)', backgroundColor: 'var(--bg-soft)', color: 'var(--text-secondary)', fontSize: '0.875rem', fontWeight: 700 }}>
                  {providerChangeStatus === 'pending'
                    ? 'Waiting for Finance or Superadmin approval.'
                    : providerChangeStatus === 'approved'
                      ? 'Approved. Waiting for an approver to apply the change.'
                      : `Decision: ${providerChangeStatusLabel}`}
                </div>
              )}
            </div>
          )}



      {canEditDetails && showEditModal && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 1000, backgroundColor: 'rgba(var(--neutral-rgb), 0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}>
          <div className="card" style={{ width: 'min(760px, 100%)', padding: '1.25rem', maxHeight: 'calc(100vh - 2rem)', overflowY: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem', alignItems: 'flex-start', marginBottom: '1rem' }}>
              <div>
                <h3 style={{ margin: 0, color: 'var(--text-primary)', fontSize: '1.1rem', fontWeight: 900 }}>Edit Ticket</h3>
                <p style={{ margin: '0.25rem 0 0 0', color: 'var(--text-secondary)', fontSize: '0.85rem' }}>Update the pending ticket details.</p>
              </div>
              <button type="button" className="btn btn-secondary" disabled={editSaving} onClick={() => setShowEditModal(false)}>
                Close
              </button>
            </div>

            {editError && (
              <div style={{ marginBottom: '1rem', padding: '0.75rem 0.9rem', borderRadius: '8px', border: '1px solid rgba(239, 68, 68, 0.35)', backgroundColor: 'rgba(239, 68, 68, 0.08)', color: 'var(--error)', fontSize: '0.875rem', fontWeight: 700 }}>
                {editError}
              </div>
            )}

            <div style={{ display: 'grid', gap: '0.85rem' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '0.35rem', fontSize: '0.78rem', fontWeight: 800, color: 'var(--text-secondary)', textTransform: 'uppercase' }}>Name *</label>
                <input className="input" value={editForm.title} onChange={(e) => updateEditForm('title', e.target.value)} placeholder="Ticket name" disabled={editSaving} />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '0.35rem', fontSize: '0.78rem', fontWeight: 800, color: 'var(--text-secondary)', textTransform: 'uppercase' }}>Description</label>
                <textarea className="input" rows="4" value={editForm.description} onChange={(e) => updateEditForm('description', e.target.value)} placeholder="Add context, reproduction steps, or handoff notes..." disabled={editSaving} />
              </div>

              {ticket.ticket_type === 'manual' && hasPermission('merchants', 'view') && (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '0.75rem' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '0.35rem', fontSize: '0.78rem', fontWeight: 800, color: 'var(--text-secondary)', textTransform: 'uppercase' }}>Merchant</label>
                    <div style={{ position: 'relative' }}>
                      <input
                        className="input"
                        value={editMerchantSearch}
                        onChange={(e) => updateEditMerchantSearch(e.target.value)}
                        placeholder={loadingMerchants ? 'Loading merchants...' : 'Search merchant by name or code...'}
                        disabled={editSaving || loadingMerchants}
                      />
                      {editMerchantSearch.trim() && !editMerchantSearchMatchesSelection && (
                        <div style={{ position: 'absolute', top: 'calc(100% + 6px)', left: 0, right: 0, zIndex: 30, display: 'grid', gap: '0.25rem', maxHeight: '260px', overflowY: 'auto', padding: '0.35rem', border: '1px solid var(--border-light)', borderRadius: '8px', backgroundColor: 'var(--bg-card)', boxShadow: '0 12px 28px rgba(var(--neutral-rgb), 0.12)' }}>
                          {filteredEditMerchants.slice(0, 10).length === 0 ? (
                            <div style={{ padding: '0.65rem 0.75rem', color: 'var(--text-muted)', fontSize: '0.82rem' }}>No merchant matches this search.</div>
                          ) : filteredEditMerchants.slice(0, 10).map(merchant => (
                            <button
                              key={merchant._id}
                              type="button"
                              disabled={editSaving}
                              onClick={() => selectEditMerchant(merchant)}
                              style={{ display: 'grid', gap: '0.05rem', minHeight: '38px', padding: '0.5rem 0.6rem', borderRadius: '6px', border: '1px solid transparent', backgroundColor: 'transparent', color: 'var(--text-primary)', cursor: editSaving ? 'not-allowed' : 'pointer', textAlign: 'left' }}
                            >
                              <span style={{ fontWeight: 900, fontSize: '0.82rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{merchant.name || merchant.merchant_code}</span>
                              <span style={{ color: 'var(--text-muted)', fontSize: '0.7rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{merchant.merchant_code || merchant.client_code || 'No code'}</span>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '0.35rem', fontSize: '0.78rem', fontWeight: 800, color: 'var(--text-secondary)', textTransform: 'uppercase' }}>Provider</label>
                    <div style={{ position: 'relative' }}>
                      <input
                        className="input"
                        value={editProviderSearch}
                        onChange={(e) => updateEditProviderSearch(e.target.value)}
                        placeholder={!editForm.merchant_id ? 'Select merchant first' : loadingProviders ? 'Loading providers...' : 'Search provider...'}
                        disabled={editSaving || !editForm.merchant_id || loadingProviders}
                      />
                      {editProviderSearch.trim() && editForm.merchant_id && !editProviderSearchMatchesSelection && (
                        <div style={{ position: 'absolute', top: 'calc(100% + 6px)', left: 0, right: 0, zIndex: 30, display: 'grid', gap: '0.25rem', maxHeight: '260px', overflowY: 'auto', padding: '0.35rem', border: '1px solid var(--border-light)', borderRadius: '8px', backgroundColor: 'var(--bg-card)', boxShadow: '0 12px 28px rgba(var(--neutral-rgb), 0.12)' }}>
                          {filteredEditProviders.slice(0, 10).length === 0 ? (
                            <div style={{ padding: '0.65rem 0.75rem', color: 'var(--text-muted)', fontSize: '0.82rem' }}>No provider matches this search.</div>
                          ) : filteredEditProviders.slice(0, 10).map(item => {
                            const provider = getProviderItem(item);
                            return (
                              <button
                                key={provider._id}
                                type="button"
                                disabled={editSaving}
                                onClick={() => selectEditProvider(item)}
                                style={{ display: 'grid', gap: '0.05rem', minHeight: '38px', padding: '0.5rem 0.6rem', borderRadius: '6px', border: '1px solid transparent', backgroundColor: 'transparent', color: 'var(--text-primary)', cursor: editSaving ? 'not-allowed' : 'pointer', textAlign: 'left' }}
                              >
                                <span style={{ fontWeight: 900, fontSize: '0.82rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{getProviderLabel(item)}</span>
                                <span style={{ color: 'var(--text-muted)', fontSize: '0.7rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{[provider.type, provider.source?.name].filter(Boolean).join(' · ') || provider.code || 'Provider'}</span>
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: '0.75rem' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.35rem', fontSize: '0.78rem', fontWeight: 800, color: 'var(--text-secondary)', textTransform: 'uppercase' }}>Priority</label>
                  <select className="input" value={editForm.priority} onChange={(e) => updateEditForm('priority', e.target.value)} disabled={editSaving}>
                    <option value="low">Low</option>
                    <option value="normal">Normal</option>
                    <option value="high">High</option>
                    <option value="urgent">Urgent</option>
                  </select>
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.35rem', fontSize: '0.78rem', fontWeight: 800, color: 'var(--text-secondary)', textTransform: 'uppercase' }}>Due Date</label>
                  <input className="input" type="date" value={editForm.due_date} onChange={(e) => updateEditForm('due_date', e.target.value)} disabled={editSaving} />
                </div>
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem', marginTop: '1.25rem', flexWrap: 'wrap' }}>
              <button type="button" className="btn btn-secondary" disabled={editSaving} onClick={() => setShowEditModal(false)}>
                Cancel
              </button>
              <button type="button" className="btn btn-primary" disabled={editSaving} onClick={handleEditTicket}>
                {editSaving ? 'Saving...' : 'Save Ticket'}
              </button>
            </div>
          </div>
        </div>
      )}
        </div>
          <TicketConversation key={ticket._id} ticket={ticket} currentUserId={currentAdminId}
            getEntryTitle={getJournalTitle} getEntryChanges={getJournalChanges} onImagePreview={openJournalImagePreview}>
            {canWriteWorkflowNote && <TicketCommentComposer key={ticket._id} ticketId={ticket._id} onUpdate={setTicket}
              disabled={!!savingAction} saving={savingAction === 'commented'}
              onBusyChange={busy => setSavingAction(busy ? 'commented' : '')} />}
          </TicketConversation>
        </div>
      </div>

          {showWorkflowDialog && actionOptions.length > 0 && (
          <TicketActionDialog title={workflowDialogTitle} busy={!!savingAction} onClose={() => setShowWorkflowDialog(false)}>
          <WorkflowActionPanel
            actions={actionOptions}
            selectedAction={selectedAction}
            selectedActionKey={selectedActionKey}
            onActionChange={(actionKey) => {
              setSelectedActionKey(actionKey);
              setActionForm(prev => ({ ...prev, assigned_to_user: actionKey === 'assign' ? assignedId : '', stage: ticket.stage, status: ticket.status, note: '' }));
              setAssigneeSearch(actionKey === 'assign' && assignedId ? currentAssigneeLabel : '');
              setAssigneeRoleFilter('all');
              setActionError('');
              setActionNotice(null);
            }}
            selectedActionDisabledReason={selectedActionDisabledReason}
            editing={editingRequest}
            previousOwners={previousOwners}
            selectedActionNeedsAssignee={selectedActionNeedsAssignee}
            selectedActionNeedsNote={selectedActionNeedsNote}
            selectedActionNeedsClosePermission={selectedActionNeedsClosePermission}
            selectedActionCanSelectRoute={selectedActionCanSelectRoute}
            showRouteFields={showRouteFields}
            workingStatusesOnly={selectedAction?.type === 'collaboration'}
            statusOptions={selectedAction?.type === 'collaboration' ? workflow.collaboration_statuses || [] : statusOptions}
            routeStatusValue={routeStatusValue}
            previewStatusLabel={previewStatusLabel}
            canCloseRoute={canCloseTicket}
            formatRouteLabel={formatTicketLabel}
            onStatusChange={(status) => {
              setActionError('');
              setActionNotice(null);
              setActionForm(prev => ({ ...prev, status }));
            }}
            currentOwnerLabel={currentAssigneeLabel}
            previewOwnerLabel={previewAssigneeLabel}
            showAssigneeField={showAssigneeField}
            assigneeHelperText={assigneeHelperText}
            canAssignToMe={canAssignToMe}
            currentUserId={admin?._id || ''}
            selectedAssignee={selectedAssignee}
            onAssigneeChange={selectAssignee}
            assigneeSearch={assigneeSearch}
            onAssigneeSearchChange={handleAssigneeSearchChange}
            roleOptions={roleOptions}
            assigneeRoleFilter={assigneeRoleFilter}
            onAssigneeRoleFilterChange={setAssigneeRoleFilter}
            filteredAssignees={filteredAssignees}
            noteValue={actionForm.note}
            onNoteChange={(note) => {
              setActionError('');
              setActionNotice(null);
              setActionForm(prev => ({ ...prev, note }));
            }}
            canWriteNote={canWriteWorkflowNote}
            actionNotice={actionNotice}
            actionError={actionError}
            canRunWorkflow={canRunWorkflow}
            canAssignTicket={canAssignTicket}
            savingAction={savingAction}
            submitDisabled={submitDisabled}
            submitLabel={submitLabel}
            onSubmit={() => handleAction(selectedAction)}
            onClose={() => setShowWorkflowDialog(false)}
          />
          </TicketActionDialog>
          )}

      {selectedJournalPreview && (
        <div
          role="dialog"
          aria-modal="true"
          style={{ position: 'fixed', inset: 0, zIndex: 1200, backgroundColor: 'rgba(var(--neutral-rgb), 0.82)', display: 'grid', gridTemplateRows: 'auto minmax(0, 1fr) auto', padding: '1rem' }}
          onClick={() => setJournalPreview({ images: [], index: -1 })}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '1rem', color: '#fff' }}>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: '0.92rem', fontWeight: 900, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{selectedJournalPreview.name}</div>
              <div style={{ marginTop: '0.2rem', fontSize: '0.75rem', opacity: 0.78 }}>{journalPreview.index + 1} / {journalPreviewImages.length} | {formatFileSize(selectedJournalPreview.size)}</div>
            </div>
            <button type="button" className="btn" style={{ color: '#fff', borderColor: 'rgba(255,255,255,0.35)', backgroundColor: 'rgba(255,255,255,0.12)' }} onClick={(event) => { event.stopPropagation(); setJournalPreview({ images: [], index: -1 }); }}>
              Close
            </button>
          </div>

          <div style={{ position: 'relative', minHeight: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem 3.25rem' }} onClick={(event) => event.stopPropagation()}>
            {journalPreviewImages.length > 1 && (
              <button type="button" aria-label="Previous image" className="btn" style={{ position: 'absolute', left: 0, top: '50%', transform: 'translateY(-50%)', width: '42px', height: '42px', padding: 0, color: '#fff', borderColor: 'rgba(255,255,255,0.35)', backgroundColor: 'rgba(255,255,255,0.12)' }} onClick={() => moveJournalPreview(-1)}>
                {'<'}
              </button>
            )}
            <img src={selectedJournalPreview.preview_url} alt={selectedJournalPreview.name} style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', borderRadius: '8px', boxShadow: '0 24px 80px rgba(0,0,0,0.35)' }} />
            {journalPreviewImages.length > 1 && (
              <button type="button" aria-label="Next image" className="btn" style={{ position: 'absolute', right: 0, top: '50%', transform: 'translateY(-50%)', width: '42px', height: '42px', padding: 0, color: '#fff', borderColor: 'rgba(255,255,255,0.35)', backgroundColor: 'rgba(255,255,255,0.12)' }} onClick={() => moveJournalPreview(1)}>
                {'>'}
              </button>
            )}
          </div>

          <div style={{ display: 'flex', justifyContent: 'center', gap: '0.4rem', flexWrap: 'wrap' }} onClick={(event) => event.stopPropagation()}>
            {journalPreviewImages.map((image, imageIndex) => (
              <button
                key={`journal-preview-thumb-${image.object_key || image.file_path || image.file_name || imageIndex}`}
                type="button"
                aria-label={`Open image ${imageIndex + 1}`}
                onClick={() => setJournalPreview(prev => ({ ...prev, index: imageIndex }))}
                style={{ width: '54px', height: '40px', padding: 0, borderRadius: '6px', overflow: 'hidden', border: imageIndex === journalPreview.index ? '2px solid #fff' : '1px solid rgba(255,255,255,0.35)', backgroundColor: 'rgba(255,255,255,0.12)', cursor: 'pointer' }}
              >
                <img src={image.preview_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default TicketDetail;
